import { CreateResult } from "react-admin";
import { getSessionItem } from "../authSession";

export type MediaUploadProgress = {
  loaded: number;
  total: number;
  progress: number | null;
};

export type MediaUploadOptions = {
  onProgress?: (progress: MediaUploadProgress) => void;
  signal?: AbortSignal;
};

const configuredApiBase = process.env.REACT_APP_NEST_API
  ? process.env.REACT_APP_NEST_API.trim()
  : "";

const uploadApiBase = (
  process.env.NODE_ENV === "production" ? "/api" : configuredApiBase || "/api"
).replace(/\/$/, "");

export default async function imageUploaderProvider(params: any) {
  const paramsCopy = { ...params };
  for (const key of Object.keys(params.data)) {
    if (paramsCopy.data[key] && paramsCopy.data[key].rawFile instanceof File) {
      const file: File = paramsCopy.data[key].rawFile;
      const form = new FormData();
      form.append("file", file);

      const response = await fetch(`${uploadApiBase}/s3/upload`, {
        method: "POST",
        body: form,
        headers: {
          Authorization: `Bearer ${getSessionItem("accessToken")}`,
        },
      });
      const uploadedFile = await response.json();

      delete paramsCopy.data[key];
      paramsCopy.data[key] = uploadedFile.Location;
    }
  }
  return paramsCopy;
}

function abortError() {
  const error = new Error("Subida cancelada");
  error.name = "AbortError";
  return error;
}

function timeoutError() {
  return new Error(
    "La subida ha superado el tiempo de espera. Comprueba el contenido del usuario antes de reintentar."
  );
}

function getUploadError(xhr: XMLHttpRequest) {
  if (xhr.status === 504) {
    return timeoutError();
  }

  try {
    const response: { message?: string | string[] } = JSON.parse(
      xhr.responseText
    );
    const message = Array.isArray(response.message)
      ? response.message.join(". ")
      : response.message;
    if (message) return new Error(message);
  } catch {
    // El proxy puede devolver HTML; se usa el mensaje según el estado.
  }

  if (!xhr.status) {
    return new Error(
      "No se pudo conectar con el servidor. Comprueba la conexión e inténtalo de nuevo."
    );
  }
  return new Error(`No se pudieron subir los archivos (HTTP ${xhr.status})`);
}

export function uploadMediaRequest(
  url: string,
  form: FormData,
  accessToken: string | null,
  options: MediaUploadOptions = {},
  xhrFactory: () => XMLHttpRequest = () => new XMLHttpRequest()
): Promise<{ count: number }> {
  return new Promise((resolve, reject) => {
    const xhr = xhrFactory();
    let lastTotal = 0;

    const cleanup = () => {
      options.signal?.removeEventListener("abort", abortUpload);
    };
    const finishWithError = (error: Error) => {
      cleanup();
      reject(error);
    };
    const abortUpload = () => xhr.abort();

    xhr.open("POST", url);
    xhr.timeout = 30 * 60 * 1000;

    if (accessToken) {
      xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
    }

    xhr.upload.onprogress = (event) => {
      const total = event.lengthComputable ? event.total : 0;
      lastTotal = total || lastTotal;
      options.onProgress?.({
        loaded: event.loaded,
        total,
        progress: total > 0 ? Math.min(event.loaded / total, 1) : null,
      });
    };
    xhr.upload.onload = (event) => {
      const total = event.lengthComputable ? event.total : lastTotal;
      if (total > 0) {
        options.onProgress?.({ loaded: total, total, progress: 1 });
      }
    };
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        finishWithError(getUploadError(xhr));
        return;
      }

      try {
        const response: { count?: number } = xhr.responseText
          ? JSON.parse(xhr.responseText)
          : {};
        cleanup();
        resolve({ count: response.count || 0 });
      } catch {
        finishWithError(
          new Error("El servidor devolvió una respuesta no válida.")
        );
      }
    };
    xhr.onerror = () => finishWithError(getUploadError(xhr));
    xhr.onabort = () => finishWithError(abortError());
    xhr.ontimeout = () => finishWithError(timeoutError());

    if (options.signal?.aborted) {
      finishWithError(abortError());
      return;
    }

    options.signal?.addEventListener("abort", abortUpload, { once: true });
    xhr.send(form);
  });
}

export async function mediaUploader(
  resource: any,
  params: any,
  options: MediaUploadOptions = {}
): Promise<CreateResult<any>> {
  const form = new FormData();
  const files: File[] = params.data.images.map(
    (file: { rawFile: File }) => file.rawFile
  );
  form.append("categoryImage", params.data.categoryImage);
  form.append("categoryVideo", params.data.categoryVideo);
  files.forEach((file) => form.append("files", file));

  const response = await uploadMediaRequest(
    `${uploadApiBase}/s3/uploadMedia/${params.data.id}/${params.data.semana}`,
    form,
    getSessionItem("accessToken"),
    options
  );
  if (response.count <= 0) {
    throw new Error("El servidor no guardó ningún archivo");
  }
  return { data: { id: Math.floor(Math.random() * 1000) } };
}
